import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  CircularProgress,
  FormHelperText
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useNavigate, useParams } from 'react-router-dom';
import Alert from '@material-ui/lab/Alert';
import { firestore } from '../../firebase/config';
import { collection, doc, getDoc, updateDoc, getDocs } from 'firebase/firestore';
import VehicleImageUpload from '../../components/VehicleImageUpload';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  paper: {
    width: '100%',
    padding: theme.spacing(3),
  },
  form: {
    width: '100%',
    marginTop: theme.spacing(2),
  },
  formControl: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  buttons: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(3),
  },
  button: {
    marginLeft: theme.spacing(1),
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
}));

const VehicleEditPage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    registrationNumber: '',
    type: '',
    capacity: '',
    status: 'Active',
    companyId: '',
    companyName: '',
    manufactureYear: '',
    model: '',
    color: '',
    fuelType: '',
    features: '',
    maintenanceDate: '',
    images: [], // URLs des images
  });
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    fetchCompanies();
    fetchVehicle();
  }, [id]);

  const fetchCompanies = async () => {
    try {
      // Simulation de récupération des données depuis Firestore
      // Dans une implémentation réelle, cela serait remplacé par un appel à l'API
      const companiesCollection = collection(firestore, 'companies');
      const companiesSnapshot = await getDocs(companiesCollection);
      
      // Si la collection n'existe pas encore ou est vide, utiliser des données de démonstration
      if (companiesSnapshot.empty) {
        const demoCompanies = [
          { id: '1', name: 'Voyage Express' },
          { id: '2', name: 'TransportCo' },
          { id: '3', name: 'EcoTravel' },
        ];
        setCompanies(demoCompanies);
      } else {
        const fetchedCompanies = companiesSnapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name
        }));
        setCompanies(fetchedCompanies);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la récupération des entreprises',
        severity: 'error'
      });
    } finally {
      setLoadingCompanies(false);
    }
  };

  const fetchVehicle = async () => {
    try {
      // Simulation de récupération depuis Firestore
      // Dans une implémentation réelle, cela serait remplacé par un appel à l'API
      const vehicleDoc = doc(firestore, 'vehicles', id);
      const vehicleSnapshot = await getDoc(vehicleDoc);
      
      if (vehicleSnapshot.exists()) {
        const vehicleData = vehicleSnapshot.data();
        setFormData({
          ...vehicleData,
          capacity: vehicleData.capacity?.toString() || '',
          manufactureYear: vehicleData.manufactureYear?.toString() || '',
          maintenanceDate: vehicleData.maintenanceDate || ''
        });
      } else {
        // Si le véhicule n'existe pas, utiliser des données de démonstration
        const demoVehicle = {
          id: id,
          name: 'Bus Premium',
          registrationNumber: 'AB-123-CD',
          type: 'Bus',
          capacity: '50',
          status: 'Active',
          companyId: '1',
          companyName: 'Voyage Express',
          manufactureYear: '2020',
          model: 'Mercedes Tourismo',
          color: 'Blanc',
          fuelType: 'Diesel',
          features: 'WiFi, Climatisation, Prises USB, Toilettes',
          maintenanceDate: '2023-01-15'
        };
        setFormData(demoVehicle);
      }
    } catch (error) {
      console.error('Error fetching vehicle:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la récupération des données du véhicule',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Traitement spécial pour le champ companyId
    if (name === 'companyId') {
      const selectedCompany = companies.find(company => company.id === value);
      setFormData({
        ...formData,
        companyId: value,
        companyName: selectedCompany ? selectedCompany.name : ''
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Le nom est requis';
    }
    
    if (!formData.registrationNumber.trim()) {
      newErrors.registrationNumber = 'L\'immatriculation est requise';
    }
    
    if (!formData.type) {
      newErrors.type = 'Le type est requis';
    }
    
    if (!formData.capacity) {
      newErrors.capacity = 'La capacité est requise';
    } else if (isNaN(formData.capacity) || parseInt(formData.capacity) <= 0) {
      newErrors.capacity = 'La capacité doit être un nombre positif';
    }
    
    if (!formData.companyId) {
      newErrors.companyId = 'L\'entreprise est requise';
    }
    
    if (!formData.status) {
      newErrors.status = 'Le statut est requis';
    }
    
    if (formData.manufactureYear && (isNaN(formData.manufactureYear) || parseInt(formData.manufactureYear) < 1900 || parseInt(formData.manufactureYear) > new Date().getFullYear())) {
      newErrors.manufactureYear = 'Année de fabrication invalide';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    try {
      // Simulation de mise à jour dans Firestore
      // Dans une implémentation réelle, cela serait remplacé par un appel à l'API
      const vehicleDoc = doc(firestore, 'vehicles', id);
      await updateDoc(vehicleDoc, {
        ...formData,
        capacity: parseInt(formData.capacity),
        manufactureYear: formData.manufactureYear ? parseInt(formData.manufactureYear) : null,
        updatedAt: new Date()
      });
      
      setSnackbar({
        open: true,
        message: 'Véhicule mis à jour avec succès',
        severity: 'success'
      });
      
      // Rediriger vers la liste après un court délai
      setTimeout(() => {
        navigate('/admin/vehicles');
      }, 1500);
      
    } catch (error) {
      console.error('Error updating vehicle:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la mise à jour du véhicule',
        severity: 'error'
      });
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/vehicles');
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box className={classes.root}>
      <Typography variant="h4" component="h1" gutterBottom>
        Modifier le véhicule
      </Typography>
      
      <Paper className={classes.paper}>
        {loading ? (
          <div className={classes.loading}>
            <CircularProgress />
          </div>
        ) : (
          <form className={classes.form} onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  name="name"
                  label="Nom du véhicule"
                  variant="outlined"
                  fullWidth
                  required
                  value={formData.name}
                  onChange={handleChange}
                  error={!!errors.name}
                  helperText={errors.name}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  name="registrationNumber"
                  label="Numéro d'immatriculation"
                  variant="outlined"
                  fullWidth
                  required
                  value={formData.registrationNumber}
                  onChange={handleChange}
                  error={!!errors.registrationNumber}
                  helperText={errors.registrationNumber}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl variant="outlined" className={classes.formControl} error={!!errors.type}>
                  <InputLabel id="type-label">Type de véhicule</InputLabel>
                  <Select
                    labelId="type-label"
                    id="type"
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    label="Type de véhicule"
                    required
                  >
                    <MenuItem value="Bus">Bus</MenuItem>
                    <MenuItem value="Minibus">Minibus</MenuItem>
                    <MenuItem value="Van">Van</MenuItem>
                    <MenuItem value="Car">Voiture</MenuItem>
                    <MenuItem value="Other">Autre</MenuItem>
                  </Select>
                  {errors.type && <FormHelperText>{errors.type}</FormHelperText>}
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  name="capacity"
                  label="Capacité (nombre de places)"
                  variant="outlined"
                  fullWidth
                  required
                  type="number"
                  value={formData.capacity}
                  onChange={handleChange}
                  error={!!errors.capacity}
                  helperText={errors.capacity}
                  inputProps={{ min: 1 }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl variant="outlined" className={classes.formControl} error={!!errors.companyId}>
                  <InputLabel id="company-label">Entreprise</InputLabel>
                  <Select
                    labelId="company-label"
                    id="companyId"
                    name="companyId"
                    value={formData.companyId}
                    onChange={handleChange}
                    label="Entreprise"
                    required
                    disabled={loadingCompanies}
                  >
                    {loadingCompanies ? (
                      <MenuItem value="">Chargement...</MenuItem>
                    ) : (
                      companies.map(company => (
                        <MenuItem key={company.id} value={company.id}>
                          {company.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  {errors.companyId && <FormHelperText>{errors.companyId}</FormHelperText>}
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl variant="outlined" className={classes.formControl} error={!!errors.status}>
                  <InputLabel id="status-label">Statut</InputLabel>
                  <Select
                    labelId="status-label"
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    label="Statut"
                    required
                  >
                    <MenuItem value="Active">Actif</MenuItem>
                    <MenuItem value="Inactive">Inactif</MenuItem>
                    <MenuItem value="Maintenance">En maintenance</MenuItem>
                  </Select>
                  {errors.status && <FormHelperText>{errors.status}</FormHelperText>}
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  name="model"
                  label="Modèle"
                  variant="outlined"
                  fullWidth
                  value={formData.model}
                  onChange={handleChange}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  name="manufactureYear"
                  label="Année de fabrication"
                  variant="outlined"
                  fullWidth
                  type="number"
                  value={formData.manufactureYear}
                  onChange={handleChange}
                  error={!!errors.manufactureYear}
                  helperText={errors.manufactureYear}
                  inputProps={{ min: 1900, max: new Date().getFullYear() }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  name="color"
                  label="Couleur"
                  variant="outlined"
                  fullWidth
                  value={formData.color}
                  onChange={handleChange}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl variant="outlined" className={classes.formControl}>
                  <InputLabel id="fuelType-label">Type de carburant</InputLabel>
                  <Select
                    labelId="fuelType-label"
                    id="fuelType"
                    name="fuelType"
                    value={formData.fuelType}
                    onChange={handleChange}
                    label="Type de carburant"
                  >
                    <MenuItem value="">Sélectionner</MenuItem>
                    <MenuItem value="Diesel">Diesel</MenuItem>
                    <MenuItem value="Essence">Essence</MenuItem>
                    <MenuItem value="Électrique">Électrique</MenuItem>
                    <MenuItem value="Hybride">Hybride</MenuItem>
                    <MenuItem value="Gaz">Gaz</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  name="features"
                  label="Caractéristiques"
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={4}
                  value={formData.features}
                  onChange={handleChange}
                  placeholder="WiFi, Climatisation, Prises USB, etc."
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  name="maintenanceDate"
                  label="Date de dernière maintenance"
                  variant="outlined"
                  fullWidth
                  type="date"
                  value={formData.maintenanceDate}
                  onChange={handleChange}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
            </Grid>
            
            {/* Upload d'images */}
            <VehicleImageUpload
              vehicleId={id}
              images={formData.images}
              onChange={(newImages) => setFormData({ ...formData, images: newImages })}
              maxImages={5}
            />
            
            <div className={classes.buttons}>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleCancel}
                className={classes.button}
              >
                Annuler
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                className={classes.button}
              >
                Enregistrer
              </Button>
            </div>
          </form>
        )}
      </Paper>
      
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VehicleEditPage;
